import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  customerNotificationService,
  type NotificationItem,
} from '@/services/customer-notification.service';
import { resolveCustomerNotificationDestination } from './customer-notification-destination';

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * B6.19 / B6.18 CU-1 — how the customer learns a Service Report is available.
 *
 * B6.16 proved a customer CAN download a published report. Nothing told them one
 * existed, so they learned by telephone. This is the guaranteed in-app channel.
 *
 * ── NO POLLING, AND NO REALTIME EITHER ──────────────────────────────────────
 * There is deliberately no socket here. The Socket.IO gateway is staff-only by
 * cryptographic separation — customer tokens are signed with a different secret
 * and carry `type: customer_access`, so they cannot verify against it, and
 * B6.19 does not weaken that boundary to add a bell. The badge refreshes on
 * mount and on navigation, which is the right cadence for a portal a customer
 * visits rather than sits in all day.
 *
 * ── CLICKING A NOTIFICATION ─────────────────────────────────────────────────
 * Mirrors the staff bell: mark read (unchanged), then open the job the
 * notification is about, resolved from the notification's own payload by
 * `resolveCustomerNotificationDestination()`. Telling a customer a report is
 * available and leaving them to find the job themselves was the gap.
 *
 * Presentation (premium UI pass): the panel is a full-width sheet under the
 * header on phones and a 22rem popover from `sm`; every class now resolves to
 * a real customer token (the previous `customer-muted`/`customer-fg`/
 * `customer-accent` names were never defined).
 */
export function CustomerNotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-notifications'],
    queryFn: () => customerNotificationService.list(15),
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => customerNotificationService.markRead(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customer-notifications'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const unread = data?.unreadCount ?? 0;
  const items: NotificationItem[] = data?.items ?? [];

  const handleActivate = (n: NotificationItem, destination: string | null) => {
    if (n.status === 'PENDING') markRead.mutate(n.id);
    if (destination) {
      setOpen(false);
      navigate(destination);
    }
  };

  return (
    <div className="sm:relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="customer-pressable relative flex h-11 w-11 items-center justify-center rounded-customer-sm text-customer-text-secondary transition-colors hover:bg-customer-text/[0.06] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <FontAwesomeIcon icon={faBell} className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-customer-danger px-1 text-[10px] font-semibold leading-none text-customer-danger-ink ring-2 ring-customer-surface"
            data-testid="customer-notification-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="customer-modal-in fixed inset-x-3 top-[calc(var(--customer-header-h)+0.5rem)] z-50 overflow-hidden rounded-customer-lg border border-customer-border bg-customer-surface shadow-customer-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1.5 sm:w-[22rem]">
          <div className="flex items-center justify-between border-b border-customer-border px-4 py-2.5">
            <h3 className="text-sm font-semibold text-customer-text">
              Notifications
              {unread > 0 && (
                <span className="ml-2 rounded-full bg-customer-primary/10 px-2 py-0.5 text-[11px] font-semibold text-customer-primary-text">
                  {unread} new
                </span>
              )}
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close notifications"
              className="flex h-9 w-9 items-center justify-center rounded-customer-sm text-customer-text-secondary hover:bg-customer-text/[0.06] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring sm:hidden"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[min(24rem,70vh)] overflow-y-auto">
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1].map((i) => (
                  <div key={i} className="customer-shimmer h-12 rounded-customer-sm" />
                ))}
              </div>
            ) : isError ? (
              <p className="px-4 py-8 text-center text-sm text-customer-danger-text">
                Could not load notifications.
              </p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-customer-text-secondary">
                You&apos;re all caught up.
              </p>
            ) : (
              <ul className="divide-y divide-customer-border">
                {items.map((n) => {
                  const destination = resolveCustomerNotificationDestination(n);
                  const isInteractive = destination !== null || n.status === 'PENDING';
                  const isUnread = n.status === 'PENDING';

                  const body = (
                    <>
                      <span
                        className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
                          isUnread ? 'bg-customer-primary' : 'bg-transparent'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-sm text-customer-text ${
                              isUnread ? 'font-semibold' : 'font-medium'
                            }`}
                          >
                            {n.title}
                          </p>
                          <span className="shrink-0 text-[11px] text-customer-text-secondary">
                            {relativeTime(n.createdAt)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs leading-4 text-customer-text-secondary">
                          {n.message}
                        </p>
                      </div>
                      {destination && (
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          aria-hidden="true"
                          className="h-3 w-3 shrink-0 self-center text-customer-text-secondary"
                        />
                      )}
                    </>
                  );

                  return (
                    <li key={n.id} className={isUnread ? 'bg-customer-primary/[0.04]' : undefined}>
                      {isInteractive ? (
                        <button
                          type="button"
                          data-testid={`customer-notification-item-${n.id}`}
                          onClick={() => handleActivate(n, destination)}
                          className={`customer-pressable flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-customer-text/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring ${
                            destination ? 'cursor-pointer' : ''
                          }`}
                        >
                          {body}
                        </button>
                      ) : (
                        <div
                          data-testid={`customer-notification-item-${n.id}`}
                          className="flex w-full items-start gap-3 px-4 py-3 text-left"
                        >
                          {body}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
